import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trackButtonClick, trackNutritionAnalysis } from '../utils/analytics';
import '../styles/SavedMeals.css';
import { Helmet } from 'react-helmet';
import { fetchRecipe, Meal, Recipe, RecipeCacheState } from '../utils/recipeUtils';

interface RecipeModalProps {
  isOpen: boolean;
  recipe: Recipe | null;
  loading: boolean;
  onClose: () => void;
}

const RecipeModal: React.FC<RecipeModalProps> = ({ isOpen, recipe, loading, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="recipe-modal-overlay">
      <div className="recipe-modal">
        <button className="recipe-modal-close" onClick={onClose}>×</button>
        
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Fetching your recipe...</p>
          </div>
        ) : recipe ? (
          <div className="recipe-content">
            <h2>{recipe.title}</h2>
            <p className="recipe-description">{recipe.description}</p>
            
            <div className="recipe-meta">
              <div className="recipe-meta-item">
                <span className="recipe-meta-label">Prep Time:</span>
                <span className="recipe-meta-value">{recipe.prepTime}</span>
              </div>
              <div className="recipe-meta-item">
                <span className="recipe-meta-label">Cook Time:</span>
                <span className="recipe-meta-value">{recipe.cookTime}</span>
              </div>
              <div className="recipe-meta-item">
                <span className="recipe-meta-label">Total Time:</span>
                <span className="recipe-meta-value">{recipe.totalTime}</span>
              </div>
              <div className="recipe-meta-item">
                <span className="recipe-meta-label">Servings:</span>
                <span className="recipe-meta-value">{recipe.servings}</span>
              </div>
            </div>
            
            <div className="recipe-section">
              <h3>Ingredients</h3>
              <ul className="recipe-ingredients" style={{ listStyleType: 'none' }}>
                {recipe.ingredients.map((ingredient, index) => {
                  // Remove any leading bullet characters or dots
                  const cleanIngredient = ingredient.replace(/^[•⦁·⚫⚪◦◉◎▪▫◆◇■□●○]?\s*/, '');
                  return <li key={index}>{cleanIngredient}</li>;
                })}
              </ul>
            </div>
            
            <div className="recipe-section">
              <h3>Instructions</h3>
              <ol className="recipe-instructions" style={{ listStyleType: 'none', paddingLeft: 0 }}>
                {recipe.instructions.map((instruction, index) => {
                  // Remove any leading numbers if present
                  const cleanInstruction = instruction.replace(/^\d+\.\s*/, '');
                  return <li key={index}>{cleanInstruction}</li>;
                })}
              </ol>
            </div>
            
            <div className="recipe-section">
              <h3>Nutrition Information</h3>
              <div className="recipe-nutrition">
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.calories}</span>
                  <span className="nutrition-label">CALORIES</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.protein}g</span>
                  <span className="nutrition-label">PROTEIN</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.carbs}g</span>
                  <span className="nutrition-label">CARBS</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-value">{recipe.nutritionInfo.fat}g</span>
                  <span className="nutrition-label">FAT</span>
                </div>
              </div>
            </div>
            
            {recipe.tips && recipe.tips.length > 0 && (
              <div className="recipe-section">
                <h3>Tips</h3>
                <ul className="recipe-tips" style={{ listStyleType: 'none' }}>
                  {recipe.tips.map((tip, index) => {
                    // Remove any leading bullet characters or dots
                    const cleanTip = tip.replace(/^[•⦁·⚫⚪◦◉◎▪▫◆◇■□●○]?\s*/, '');
                    return <li key={index}>{cleanTip}</li>;
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="recipe-error">
            <h3>Unable to generate recipe</h3>
            <p>We couldn't generate a recipe for this meal. Please try again later.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SavedMeals: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  
  // Recipe modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
  const [recipeLoading, setRecipeLoading] = useState<boolean>(false);
  const [savedRecipes, setSavedRecipes] = useState<RecipeCacheState>({});
  // Add state for meal deletion confirmation
  const [mealToDelete, setMealToDelete] = useState<string | null>(null);

  useEffect(() => {
    document.title = "My Saved Meals | AI Nutrition";
    fetchSavedMeals();
  }, [currentUser]);

  const fetchSavedMeals = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const userId = currentUser.uid;
      const apiEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/getmeals?userId=${userId}`;
      
      const response = await fetch(apiEndpoint);
      
      if (!response.ok) {
        throw new Error('Failed to fetch meals');
      }
      
      const data = await response.json();
      setMeals(data.meals || []);
    } catch (error) {
      console.error('Error fetching saved meals:', error);
      setError('Failed to load your saved meals. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleViewRecipe = async (meal: Meal) => {
    // Track viewing a saved meal recipe
    trackButtonClick('view_saved_meal_recipe', 'saved_meals');
    
    setIsModalOpen(true);
    
    await fetchRecipe(
      meal,
      savedRecipes,
      setSavedRecipes,
      setCurrentRecipe,
      setRecipeLoading
    );
  };

  const handleDeleteMeal = async (mealId: string) => {
    if (!currentUser) return;
    
    // Track deleting a saved meal
    trackButtonClick('delete_saved_meal', 'saved_meals');
    
    try {
      const userId = currentUser.uid;
      const apiEndpoint = `${process.env.REACT_APP_API_GATEWAY_URL}/prod/deletemeal`;
      
      const response = await fetch(apiEndpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          mealId
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete meal');
      }
      
      // Remove the meal from state
      setMeals(prevMeals => prevMeals.filter(meal => meal.mealId !== mealId));
      
      // Also remove the recipe if it exists
      if (savedRecipes[mealId]) {
        const newSavedRecipes = {...savedRecipes};
        delete newSavedRecipes[mealId];
        setSavedRecipes(newSavedRecipes);
      }
    } catch (error) {
      console.error('Error deleting meal:', error);
      setError('Failed to delete meal. Please try again later.');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Track closing recipe modal
    trackButtonClick('close_recipe_modal', 'saved_meals');
  };

  const renderMealCard = (meal: Meal) => {
    return (
      <div className="saved-meal-card" key={meal.mealId}>
        <div className="saved-meal-header">
          <h3 className="saved-meal-title">{meal.title}</h3>
          <span className="saved-meal-date">Saved on {meal.savedAt ? formatDate(meal.savedAt) : 'unknown date'}</span>
        </div>
        
        <div className="saved-meal-nutrition">
          <div className="nutrition-item">
            <span className="nutrition-value">{meal.calories}</span>
            <span className="nutrition-label">CALORIES</span>
          </div>
          <div className="nutrition-item">
            <span className="nutrition-value">{meal.protein}g</span>
            <span className="nutrition-label">PROTEIN</span>
          </div>
          <div className="nutrition-item">
            <span className="nutrition-value">{meal.carbs}g</span>
            <span className="nutrition-label">CARBS</span>
          </div>
          <div className="nutrition-item">
            <span className="nutrition-value">{meal.fat}g</span>
            <span className="nutrition-label">FAT</span>
          </div>
        </div>
        
        <div className="saved-meal-details">
          <div className="ingredients-section">
            <h4>Ingredients</h4>
            <ul className="ingredients-list" style={{ listStyleType: 'none' }}>
              {meal.ingredients.map((ingredient, index) => {
                // Remove any leading bullet characters or dots
                const cleanIngredient = ingredient.replace(/^[•⦁·⚫⚪◦◉◎▪▫◆◇■□●○]?\s*/, '');
                return <li key={index}>{cleanIngredient}</li>;
              })}
            </ul>
          </div>
          
          {meal.timeToPrep && (
            <div className="prep-time">
              <h4>Prep Time</h4>
              <p>{meal.timeToPrep}</p>
            </div>
          )}
          
          <div className="recipe-button-container">
            <button 
              className="recipe-button"
              onClick={() => handleViewRecipe(meal)}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
              </svg>
              View Recipe
            </button>
            <button 
              className="delete-button"
              onClick={() => {
                if (meal.mealId) {
                  setMealToDelete(meal.mealId);
                  // Track opening delete confirmation
                  trackButtonClick('open_delete_confirmation', 'saved_meals');
                }
              }}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="saved-meals-container">
      <Helmet>
        <title>My Saved Meals | AI Nutrition</title>
        <meta name="description" content="View your saved meal plans and recipes" />
      </Helmet>
      
      <div className="saved-meals-header">
        <h1>My Saved Meals</h1>
        {meals.length > 0 && (
          <p className="meals-count">You have {meals.length} saved meal{meals.length !== 1 ? 's' : ''}</p>
        )}
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your saved meals...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
          <button 
            onClick={fetchSavedMeals} 
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      ) : meals.length === 0 ? (
        <div className="empty-state">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="64" 
            height="64" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="1" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M18 8h1a4 4 0 1 1 0 8h-1"></path>
            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
            <line x1="6" y1="1" x2="6" y2="4"></line>
            <line x1="10" y1="1" x2="10" y2="4"></line>
            <line x1="14" y1="1" x2="14" y2="4"></line>
          </svg>
          <h2>No Saved Meals Yet</h2>
          <p>Start creating meal plans and save your favorite meals to see them here.</p>
          <a href="/meal-planner" className="create-plan-button">Create Meal Plan</a>
        </div>
      ) : (
        <div className="saved-meals-grid">
          {meals.map(meal => renderMealCard(meal))}
        </div>
      )}
      
      <RecipeModal 
        isOpen={isModalOpen}
        recipe={currentRecipe}
        loading={recipeLoading}
        onClose={closeModal}
      />
      
      {/* Delete Confirmation Dialog */}
      {mealToDelete && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Delete Meal</h3>
            <p>Are you sure you want to delete this meal? This action cannot be undone.</p>
            <div className="delete-confirmation-actions">
              <button 
                className="cancel-button" 
                onClick={() => {
                  setMealToDelete(null);
                  trackButtonClick('cancel_delete_meal', 'saved_meals');
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-button" 
                onClick={() => {
                  if (mealToDelete) {
                    handleDeleteMeal(mealToDelete);
                    setMealToDelete(null);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedMeals; 